/**
 * TipTap → Telegram HTML serializer (P5-D10-F5 T-P5-D10-F5-2).
 *
 * Walks a TipTap document JSON and emits a Telegram-`HTML`-parse-mode-safe
 * string using only the allowed tag set: `<b>`, `<i>`, `<u>`, `<s>`, `<code>`,
 * `<pre>`, `<blockquote>`, `<a href="...">`, `<span class="tg-spoiler">`.
 *
 * **Personalization atom node.** A node `{ type: 'firstName' }` emits the
 * literal string `{firstName}` (not the user's name). The send-time
 * personalization helper then runs `/\{firstName\}/g` against the result to
 * substitute per-recipient. Treating `firstName` as a TipTap atom node (rather
 * than a literal text fragment) lets the editor render it as a styled chip
 * while keeping the wire format stable.
 *
 * **Escape-then-wrap.** Text-node content is HTML-entity-escaped FIRST, then
 * the mark wrappers (which are serializer-owned) are applied. This means
 * `<b>` in source text becomes `&lt;b&gt;` and never collides with the
 * serializer's own `<b>` tags.
 *
 * **Strip-don't-preserve.** Unsupported node types (heading, lists, table,
 * image, hr, plus any unknown future node) are dropped entirely — children are
 * not visited, text is not preserved. Telegram clients ignore unknown tags but
 * will render preserved text in the wrong context (e.g., a heading's text
 * without its semantic role would appear as an unstyled run); stripping is the
 * safer contract.
 *
 * Spec: P5-D10-F5 §"Shared infrastructure F5 owns" lines 133–158.
 */

// =============================================================================
// Public types — moderately typed; permissive on `type: string` so the
// serializer accepts (and strips) unknown future nodes without TypeScript
// rejecting valid input.
// =============================================================================

export interface TipTapMark {
  type: string;
  attrs?: Record<string, unknown>;
}

export interface TipTapNode {
  type: string;
  text?: string;
  attrs?: Record<string, unknown>;
  marks?: TipTapMark[];
  content?: TipTapNode[];
}

export interface TipTapDocument {
  type: 'doc';
  content?: TipTapNode[];
}

// =============================================================================
// Serializer
// =============================================================================

const SUPPORTED_MARK_OPEN: Record<string, (mark: TipTapMark) => string | null> = {
  bold: () => '<b>',
  italic: () => '<i>',
  underline: () => '<u>',
  strike: () => '<s>',
  code: () => '<code>',
  link: (m) => {
    const href = typeof m.attrs?.href === 'string' ? m.attrs.href : '';
    return `<a href="${escapeAttribute(href)}">`;
  },
  tgSpoiler: () => '<span class="tg-spoiler">',
};

const SUPPORTED_MARK_CLOSE: Record<string, string> = {
  bold: '</b>',
  italic: '</i>',
  underline: '</u>',
  strike: '</s>',
  code: '</code>',
  link: '</a>',
  tgSpoiler: '</span>',
};

export function tiptapToTelegramHtml(doc: TipTapDocument): string {
  if (!doc.content || doc.content.length === 0) return '';
  // Each TipTap paragraph corresponds to one Enter in the editor. Joining
  // paragraphs with a single `\n` makes Enter render as a real line break in
  // Telegram (line directly below). An EMPTY paragraph between two non-empty
  // ones (`renderBlock` returns `''`) participates in the join and produces
  // the extra blank line a user typed by pressing Enter twice. Strip-on-
  // unsupported blocks (`renderBlock` returns `null`) drop entirely — including
  // from the join — so a heading does NOT add a phantom newline between
  // surrounding supported content.
  return doc.content
    .map(renderBlock)
    .filter((s): s is string => s !== null)
    .join('\n');
}

function renderBlock(node: TipTapNode): string | null {
  switch (node.type) {
    case 'paragraph':
      // Empty paragraphs return `''` (preserved in the join → blank line).
      return renderInlineChildren(node.content);
    case 'codeBlock':
      return `<pre>${renderRawText(node.content)}</pre>`;
    case 'blockquote':
      return `<blockquote>${renderBlockChildren(node.content)}</blockquote>`;
    case 'bulletList':
      return renderListAsPlainText(node.content, '• ');
    case 'orderedList':
      return renderListAsPlainText(node.content, null);
    default:
      // Strip every unsupported / unknown block-level node entirely. `null`
      // tells the doc-level + nested joins to drop this block — including
      // its would-be separator — so unsupported nodes never contribute
      // phantom `\n\n` to surrounding supported content.
      return null;
  }
}

/**
 * Telegram HTML mode does NOT support `<ul>` / `<ol>` / `<li>`. To preserve
 * the bullet/number affordance from the editor, list items are serialized as
 * plain-text lines with a glyph prefix (`• ` for bullets, `1. ` `2. ` ... for
 * ordered). Each item joins with `\n` so the result is a visually correct
 * vertical list in Telegram. Inline marks inside an item still serialize
 * normally (bold/italic/etc. are valid Telegram HTML inside the line).
 */
function renderListAsPlainText(
  items: TipTapNode[] | undefined,
  bulletGlyph: string | null,
): string {
  if (!items || items.length === 0) return '';
  return items
    .map((item, index) => {
      if (item.type !== 'listItem') return '';
      const inner = renderBlockChildren(item.content)
        // listItem children are wrapped in paragraphs which double-newline
        // join — collapse to single newlines inside a single bullet so the
        // item reads as one logical line.
        .replace(/\n\n+/g, '\n');
      const prefix = bulletGlyph ?? `${index + 1}. `;
      return `${prefix}${inner}`;
    })
    .filter((s) => s !== '')
    .join('\n');
}

function renderBlockChildren(children: TipTapNode[] | undefined): string {
  if (!children || children.length === 0) return '';
  // Same blank-paragraph contract as the doc-level join: empty paragraphs
  // (`''`) participate in the join so blockquotes that contain a blank
  // middle paragraph still produce the visible blank line. Unsupported
  // children (`null`) are dropped from the join entirely.
  return children
    .map(renderBlock)
    .filter((s): s is string => s !== null)
    .join('\n');
}

function renderInlineChildren(children: TipTapNode[] | undefined): string {
  if (!children || children.length === 0) return '';
  return children.map(renderInline).join('');
}

function renderInline(node: TipTapNode): string {
  if (node.type === 'text') {
    const escaped = escapeHtml(node.text ?? '');
    return wrapWithMarks(escaped, node.marks);
  }
  if (node.type === 'firstName') {
    // Atom node — emits the literal personalization token. NOT escaped (the
    // braces must survive into the dispatch-side regex substitution).
    return '{firstName}';
  }
  if (node.type === 'hardBreak') {
    return '\n';
  }
  // Strip every other inline node type (mention atoms beyond firstName,
  // images, custom emoji, etc.). Future personalization atoms get a case
  // here when added.
  return '';
}

function wrapWithMarks(text: string, marks: TipTapMark[] | undefined): string {
  if (!marks || marks.length === 0) return text;
  let out = text;
  // Apply marks inside-out: iterate in declared order, wrapping each layer
  // around the current accumulator.
  for (const m of marks) {
    const open = SUPPORTED_MARK_OPEN[m.type]?.(m);
    const close = SUPPORTED_MARK_CLOSE[m.type];
    if (open == null || close == null) continue; // strip unknown marks
    out = `${open}${out}${close}`;
  }
  return out;
}

/**
 * `<pre>` content is concatenated text-only (mark wrappers are not applied
 * inside code blocks — Telegram's parser ignores nested formatting in `<pre>`).
 */
function renderRawText(children: TipTapNode[] | undefined): string {
  if (!children || children.length === 0) return '';
  return children
    .map((c) => (c.type === 'text' ? escapeHtml(c.text ?? '') : ''))
    .join('');
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>]/g, (c) => {
    if (c === '&') return '&amp;';
    if (c === '<') return '&lt;';
    return '&gt;';
  });
}

function escapeAttribute(s: string): string {
  return s.replace(/[&<>"]/g, (c) => {
    if (c === '&') return '&amp;';
    if (c === '<') return '&lt;';
    if (c === '>') return '&gt;';
    return '&quot;';
  });
}
